import { useState, useEffect } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import { doc, getDoc } from 'firebase/firestore'
import { db, APP_COLLECTION, APP_DOC } from '../lib/firebase'
import { getModule } from '../data/modules'
import { useConfig } from '../context/ConfigContext'
import { hydrateFromFirestore } from '../data/moduleSchema'
import ModuleLayout from '../components/ModuleLayout'
import DynamicModule from '../components/DynamicModule'
import Module0 from '../modules/Module0'
import Module1 from '../modules/Module1'
import Module2 from '../modules/Module2'
import Module3 from '../modules/Module3'
import Module4 from '../modules/Module4'
import Module5 from '../modules/Module5'
import Module6 from '../modules/Module6'
import ModuleSumifs from '../modules/ModuleSumifs'
import Module7 from '../modules/Module7'
import Module8 from '../modules/Module8'
import ModuleCombining from '../modules/ModuleCombining'
import Module9 from '../modules/Module9'
import Module10 from '../modules/Module10'
import Module11 from '../modules/Module11'
import Module12 from '../modules/Module12'
import Module13 from '../modules/Module13'
import ModuleTroubleshooting from '../modules/ModuleTroubleshooting'
import ModuleGettingSetUp from '../modules/ModuleGettingSetUp'
import ModuleWhatClaudeIs from '../modules/ModuleWhatClaudeIs'
import ModuleFirstAsk from '../modules/ModuleFirstAsk'
import ModuleAskForData from '../modules/ModuleAskForData'
import ModulePromptingForSheets from '../modules/ModulePromptingForSheets'
import ModuleFirstAppsScript from '../modules/ModuleFirstAppsScript'
import ModuleMakingItRepeatable from '../modules/ModuleMakingItRepeatable'
import ModuleGithub101 from '../modules/ModuleGithub101'

// Keyed by the module's permanent `id` in src/data/modules.js — NOT by filename.
// The numbered files are historically misaligned (Module7.jsx is module 8,
// Module13.jsx is module 15). New modules use named files instead.
const moduleComponents = {
  0: Module0,
  1: Module1,
  2: Module2,
  3: Module3,
  4: Module4,
  5: Module5,
  6: Module6,
  7: ModuleSumifs,
  8: Module7,
  9: Module8,
  10: ModuleCombining,
  11: Module9,
  12: Module10,
  13: Module11,
  14: Module12,
  15: Module13,
  16: ModuleTroubleshooting,
  17: ModuleGettingSetUp,
  18: ModuleWhatClaudeIs,
  26: ModuleGithub101,
  19: ModuleFirstAsk,
  25: ModuleAskForData,
  20: ModulePromptingForSheets,
  22: ModuleFirstAppsScript,
  23: ModuleMakingItRepeatable,
}

export default function ModuleRouter() {
  const { moduleId } = useParams()
  const { customModules } = useConfig()

  // The URL segment may be an id OR a slug, so it is resolved to the canonical
  // DOC ID before anything is fetched. The Firestore read below addresses a
  // document by id; handed `what-claude-is` it would simply miss, and the
  // module would read as deleted.
  //
  // Resolved against the already-loaded customModules rather than with a query,
  // so this costs no extra read. Falls through to the segment itself, which is
  // what a plain id URL and a built-in slug both want.
  const resolved = getModule(moduleId, customModules)
  const docId = resolved ? String(resolved.id) : String(moduleId ?? '')
  const id = parseInt(docId, 10)
  const [customModule, setCustomModule] = useState(null)
  // Starts TRUE whenever there is an id to look up, and that is load-bearing.
  //
  // The custom-module fetch is scheduled in an effect, so on the FIRST render
  // `customModule` is still null. With this flag starting false, the guard
  // below saw `isNaN(id)` — true for every `custom-…` id, since parseInt of a
  // non-numeric id is NaN — and returned <Navigate to="/modules"> before the
  // fetch it had just scheduled could resolve. Every AI Studio module was
  // therefore unreachable: clicking one from the course list or from the
  // Studio preview bounced straight back to the list, which looks exactly
  // like the module having gone missing.
  //
  // Rendering the spinner first costs a built-in module nothing it was not
  // already paying — that Firestore read happens on every module either way,
  // because a custom module overrides a built-in of the same id.
  const [loadingCustom, setLoadingCustom] = useState(Boolean(moduleId))

  // Check if standard hardcoded module exists
  const mod = !isNaN(id) ? getModule(id) : null
  const ModuleComponent = !isNaN(id) ? moduleComponents[id] : null

  // Fetch from custom_modules if exists in Firestore (takes precedence so edits to any module immediately take effect!)
  useEffect(() => {
    if (docId) {
      setLoadingCustom(true)
      getDoc(doc(db, APP_COLLECTION, APP_DOC, 'custom_modules', docId))
        .then((snap) => {
          if (snap.exists()) {
            // HYDRATED, like every other reader of this collection.
            //
            // Firestore cannot store a nested array, so sanitizeForFirestore
            // rewrites any 2D one — `page.table.rows` is the case that exists —
            // as `{ _isNestedArray: true, json: '…' }`. hydrateFromFirestore is
            // the inverse, and ConfigContext and aiStudioService both call it on
            // the way out. This component did not, so a module rendered through
            // the router got a table whose `rows` was that wrapper OBJECT: still
            // truthy, so DynamicModule's `page.table.rows &&` guard let it
            // through, then `rows.map is not a function` took down the whole
            // page. Only reachable once the module could render at all.
            //
            // The doc id is spread LAST so it always wins. The document also
            // stores an `id` field of its own — publishCustomModule writes the
            // whole schema — and those two agree only because it publishes to
            // `currentSchema.id`. The URL addresses the DOC, so if they ever
            // diverge the doc id is the true one; the other order would put a
            // stale id on this object and quietly lose prev/next for it.
            setCustomModule(hydrateFromFirestore({ ...snap.data(), id: snap.id }))
          }
        })
        .catch((err) => console.error('Error fetching custom module:', err))
        .finally(() => setLoadingCustom(false))
    }
  }, [moduleId, docId])

  if (loadingCustom) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    )
  }

  // Render custom module if loaded from Firestore.
  //
  // The module is PASSED to the layout, not looked up again. ModuleLayout
  // resolves from the hardcoded registry when it has to, and a custom module
  // is not in it — that second lookup is what rendered "Module not found."
  // over a module this component had already fetched successfully.
  if (customModule) {
    return (
      <ModuleLayout moduleId={moduleId} module={customModule}>
        <DynamicModule moduleData={customModule} />
      </ModuleLayout>
    )
  }

  if (isNaN(id) || !mod || mod.published === false) {
    return <Navigate to="/modules" replace />
  }

  if (!ModuleComponent) {
    return (
      <ModuleLayout moduleId={id}>
        <div className="text-center py-20">
          <div className="text-6xl mb-4">🚧</div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Coming Soon</h2>
          <p className="text-slate-500">This module is still being built.</p>
        </div>
      </ModuleLayout>
    )
  }

  return <ModuleComponent />
}

